import { getPlatform } from "@/platform";
import {
  parseWebDictationHistory,
  type WebDictationHistoryItem,
} from "@/web-dictation-interactions";
import {
  globalDictationPlugin,
  type GlobalDictationConfig,
  type GlobalDictationStatus,
} from "@/clients/global-dictation.plugin";

export type { GlobalDictationStatus };

const defaultStatus: GlobalDictationStatus = {
  enabled: false,
  microphoneGranted: false,
  overlayGranted: false,
  accessibilityGranted: false,
  batteryOptimizationIgnored: false,
  setupComplete: false,
};

export const isGlobalDictationAvailable = () => {
  const platform = getPlatform();
  return platform === "android" || platform === "ios";
};

export const isGlobalDictationKeyboardPlatform = () => getPlatform() === "ios";

export const getGlobalDictationStatusAsync =
  async (): Promise<GlobalDictationStatus> => {
    if (!isGlobalDictationAvailable()) return defaultStatus;

    try {
      return await globalDictationPlugin.getStatus();
    } catch (error) {
      console.error("[GlobalDictation] Failed to get status", error);
      return defaultStatus;
    }
  };

// The vocabulary lives in the app store; the controller registers a source
// here so every configure call ships the current rule table without each
// caller having to thread it through.
let _vocabularyRulesSource: (() => string) | undefined;
let _dictationLanguageSource: (() => string | undefined) | undefined;
let _recentDictationLanguagesSource: (() => string[] | undefined) | undefined;

export const configureDictationVocabularySource = (
  source: () => string,
): void => {
  _vocabularyRulesSource = source;
};

export const configureDictationLanguageSource = (
  source: () => string | undefined,
  recentSource?: () => string[] | undefined,
): void => {
  _dictationLanguageSource = source;
  _recentDictationLanguagesSource = recentSource;
};

export const configureGlobalDictationAsync = async (
  config: GlobalDictationConfig,
): Promise<GlobalDictationStatus> => {
  if (!isGlobalDictationAvailable()) return defaultStatus;

  return globalDictationPlugin.configure({
    ...config,
    dictationLanguage: config.dictationLanguage ?? _dictationLanguageSource?.(),
    recentDictationLanguages:
      config.recentDictationLanguages ?? _recentDictationLanguagesSource?.(),
    vocabularyRules: config.vocabularyRules ?? _vocabularyRulesSource?.(),
  });
};

export const setGlobalDictationEnabledAsync = async (
  enabled: boolean,
): Promise<GlobalDictationStatus> => {
  if (!isGlobalDictationAvailable()) return defaultStatus;

  return globalDictationPlugin.setEnabled({ enabled });
};

export const setGlobalDictationBubbleFaintAsync = async (
  faint: boolean,
): Promise<GlobalDictationStatus> => {
  if (getPlatform() !== "android") return defaultStatus;
  return globalDictationPlugin.setBubbleAppearance({ faint });
};

// The floating bubble's recent results, kept natively on this device only.
export const getRecentGlobalDictationsAsync = async (): Promise<
  WebDictationHistoryItem[]
> => {
  if (getPlatform() !== "android") return [];
  try {
    const { items } = await globalDictationPlugin.getRecentDictations();
    return parseWebDictationHistory(items);
  } catch (error) {
    console.error("[GlobalDictation] Failed to read recent dictations", error);
    return [];
  }
};

export const setGlobalDictationLocaleAsync = async (
  locale: string,
): Promise<void> => {
  if (getPlatform() !== "android") return;
  await globalDictationPlugin.setLocale({ locale });
};

export const requestGlobalDictationMicrophonePermissionAsync =
  async (): Promise<GlobalDictationStatus> => {
    if (!isGlobalDictationAvailable()) return defaultStatus;

    return globalDictationPlugin.requestMicrophonePermission();
  };

export const openGlobalDictationOverlaySettingsAsync = async () => {
  if (getPlatform() !== "android") return;

  await globalDictationPlugin.openOverlayPermissionSettings();
};

export const openGlobalDictationAccessibilitySettingsAsync = async () => {
  if (getPlatform() !== "android") return;

  await globalDictationPlugin.openAccessibilitySettings();
};

export const openGlobalDictationBatterySettingsAsync = async () => {
  if (getPlatform() !== "android") return;

  await globalDictationPlugin.openBatteryOptimizationSettings();
};

export const openGlobalDictationKeyboardSettingsAsync = async () => {
  if (getPlatform() !== "ios") return;

  await globalDictationPlugin.openKeyboardSettings();
};

export const startGlobalDictationFlowSessionAsync = async (): Promise<
  GlobalDictationStatus | undefined
> => {
  if (getPlatform() !== "ios") return undefined;

  try {
    return await globalDictationPlugin.startFlowSession();
  } catch (error) {
    console.error("[GlobalDictation] startFlowSession failed", error);
    return undefined;
  }
};

export const stopGlobalDictationFlowSessionAsync = async (): Promise<
  GlobalDictationStatus | undefined
> => {
  if (getPlatform() !== "ios") return undefined;

  try {
    return await globalDictationPlugin.stopFlowSession();
  } catch (error) {
    console.error("[GlobalDictation] stopFlowSession failed", error);
    return undefined;
  }
};

// Cross-platform setup check for status objects. Android status carries the
// overlay/accessibility flags; iOS carries keyboardEnabled/fullAccessGranted
// (and reports the Android flags as true). Undefined iOS flags on Android
// must not fail the check, hence the `!== false` comparisons.
export const isGlobalDictationSetupComplete = (
  status: GlobalDictationStatus | undefined,
): boolean =>
  Boolean(
    status?.microphoneGranted &&
    status?.overlayGranted &&
    status?.accessibilityGranted &&
    status?.keyboardEnabled !== false &&
    status?.fullAccessGranted !== false,
  );
